import axios from "axios";
import API_ENDPOINTS from "../config/apiEndpoints";
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  notifySessionExpired,
} from "./tokenStore";

export const APPLICATION_ID = API_ENDPOINTS.APPLICATION_ID;

const axiosInstance = axios.create({ baseURL: API_ENDPOINTS.BASE_URL });
export const axiosInstancePublic = axios.create({
  baseURL: API_ENDPOINTS.BASE_PUBLIC_URL,
});
export const axiosInstanceSecured = axios.create({
  baseURL: API_ENDPOINTS.BASE_AUTH_URL,
});

export const clearTokens = () => clearAccessToken();

const attachToken = (config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

axiosInstance.interceptors.request.use(attachToken, (error) =>
  Promise.reject(error),
);
axiosInstanceSecured.interceptors.request.use(attachToken, (error) =>
  Promise.reject(error),
);

const isSessionEnded = (error) =>
  error?.sessionInvalid ||
  error?.response?.status === 401 ||
  error?.response?.status === 403;

let refreshPromise = null;

export function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        `${API_ENDPOINTS.BASE_AUTH_URL}/${API_ENDPOINTS.REFRESH_TOKEN}`,
        null,
        { withCredentials: true },
      )
      .then((response) => {
        const newAccessToken =
          response?.data?.result?.accessToken ?? response?.data?.accessToken;
        if (!newAccessToken) {
          const err = new Error("New access token was not returned.");
          err.sessionInvalid = true;
          throw err;
        }
        setAccessToken(newAccessToken);
        return newAccessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

export function logoutApi() {
  return axios.post(
    `${API_ENDPOINTS.BASE_AUTH_URL}/${API_ENDPOINTS.LOGOUT}`,
    null,
    { withCredentials: true },
  );
}

const handle401 = (instance) => async (error) => {
  const originalRequest = error?.config;
  const status = error.response?.status;

if (
    status === 401 &&
    originalRequest &&
    !originalRequest._retry
  ) {
    originalRequest._retry = true;

    try {
      const newAccessToken = await refreshAccessToken();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return instance(originalRequest);
    } catch (refreshError) {
      if (isSessionEnded(refreshError)) {
        console.error("CF Portal session ended:", refreshError);
        clearAccessToken();
        notifySessionExpired();
      }

      return Promise.reject(refreshError?.response?.data ?? refreshError);
    }
  }

  if (error.response?.data) return Promise.reject(error.response.data);
  return Promise.reject(error);
};

axiosInstance.interceptors.response.use(
  (response) => response,
  handle401(axiosInstance),
);
axiosInstanceSecured.interceptors.response.use(
  (response) => response,
  handle401(axiosInstanceSecured),
);

export default axiosInstance;
